// News API
import api, { type AxiosResponse } from './client';
import type { NewsCategory, News, NewsEmailCustomer, QueryParams } from './types';

export const newsAPI = {
  // Categories
  getCategories: (): Promise<AxiosResponse<{ count: number; results: NewsCategory[] }>> =>
    api.get('/core/news-categories/'),

  getCategory: (id: number): Promise<AxiosResponse<NewsCategory>> =>
    api.get(`/core/news-categories/${id}/`),

  createCategory: (data: Partial<NewsCategory>): Promise<AxiosResponse<NewsCategory>> =>
    api.post('/core/news-categories/', data),

  updateCategory: (id: number, data: Partial<NewsCategory>): Promise<AxiosResponse<NewsCategory>> =>
    api.put(`/core/news-categories/${id}/`, data),

  deleteCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/news-categories/${id}/`),

  // News CRUD
  getNews: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: News[] }>> =>
    api.get('/core/news/', { params }),

  getNewsArticle: (id: number): Promise<AxiosResponse<News>> =>
    api.get(`/core/news/${id}/`),

  createNews: (data: Partial<News>): Promise<AxiosResponse<News>> =>
    api.post('/core/news/', data),

  updateNews: (id: number, data: Partial<News>): Promise<AxiosResponse<News>> =>
    api.put(`/core/news/${id}/`, data),

  deleteNews: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/news/${id}/`),

  // Read tracking
  markRead: (id: number): Promise<AxiosResponse<{ status: string; message: string }>> =>
    api.post(`/core/news/${id}/mark_read/`),

  getUnreadCount: (): Promise<AxiosResponse<{ unread_count: number; total_news: number }>> =>
    api.get('/core/news/unread_count/'),

  markAllRead: (): Promise<AxiosResponse<{ status: string; message: string; marked_count: number }>> =>
    api.post('/core/news/mark_all_read/'),

  // Email selected customers about a news article
  emailCustomers: (id: number, customerIds: number[]): Promise<AxiosResponse<{ status: string; message: string; sent_count?: number }>> =>
    api.post(`/core/news/${id}/email-customers/`, { customer_ids: customerIds }),

  // Get customer list for email picker
  getCustomersForEmail: (
    id: number,
    params?: { search?: string; is_preferred?: string; commodity_group_id?: string; commodity_type_id?: string; page?: number; page_size?: number }
  ): Promise<AxiosResponse<{ count: number; page: number; page_size: number; results: NewsEmailCustomer[] }>> =>
    api.get(`/core/news/${id}/customers-for-email/`, { params }),

  // Get commodity groups + types for email picker dropdowns
  getCommodityGroupsForEmail: (
    id: number
  ): Promise<AxiosResponse<{ commodity_groups: Array<{ id: number; name: string }>; commodity_types: Array<{ id: number; name: string; commodity_group_id: number }> }>> =>
    api.get(`/core/news/${id}/commodity-groups-for-email/`),

  // Get commodity groups + types without a saved article (for new article flow)
  getCommodityGroupsForEmailPreview: (): Promise<AxiosResponse<{ commodity_groups: Array<{ id: number; name: string }>; commodity_types: Array<{ id: number; name: string; commodity_group_id: number }> }>> =>
    api.get('/core/news/commodity-groups-for-email-preview/'),

  // Get customers for email picker without a saved article
  getCustomersForEmailPreview: (
    params?: { search?: string; is_preferred?: string; commodity_group_id?: string; commodity_type_id?: string; page?: number; page_size?: number }
  ): Promise<AxiosResponse<{ count: number; page: number; page_size: number; results: NewsEmailCustomer[] }>> =>
    api.get('/core/news/customers-for-email-preview/', { params }),

  // Send email without saving article
  emailCustomersPreview: (data: {
    title: string; summary: string; content: string; external_link?: string; customer_ids: number[];
  }): Promise<AxiosResponse<{ status: string; message: string; sent_count?: number }>> =>
    api.post('/core/news/email-customers-preview/', data),
};
